// End-of-match statistics per hero. Write-only from the simulation's side: game.ts, monsters.ts,
// artifacts.ts and skills.ts report into a StatsTracker, and nothing in core ever reads it back — so it
// can never change an outcome, and the determinism goldens (which hash sim state only) stay untouched.
import type { Team } from './layout.js';
import type { HeroKind } from './heroes.js';
import type { HeroId } from './events.js';
import type { RelicLine } from './commands.js';

/** A hero who damaged the victim this many seconds before its death (and did not land the kill) assists. */
export const ASSIST_WINDOW = 10;

export type HeroStats = {
  kills: number;
  deaths: number;
  assists: number;
  /** Hp actually removed from enemy heroes (after shields/dodges, capped at the hp they had left). */
  damageToHeroes: number;
  /** Hp actually removed from this hero, by anyone — heroes and monsters alike. */
  damageTaken: number;
  damageToMonsters: number;
  /** Hp actually restored by this hero's own skills (pools, prayers) — not fountain, regen or potions. */
  healing: number;
  /** Оберег's ward, counted at the full amount granted. */
  shielding: number;
  /** Times this hero stood on a point when it flipped to their team. */
  captures: number;
  /** Seconds spent alive inside any point's radius. */
  pointTime: number;
  monsterKills: number;
  /** Net ink this hero took from the team's shared pool: purchases minus sell refunds. */
  inkSpent: number;
  artifactsBought: number;
  bestStreak: number;
  /** This hero's passive at work, in the unit passives.ts's own `PASSIVES[kind].stat` names. */
  passive: number;
};

export type HeroStatLine = HeroStats & { id: HeroId; kind: HeroKind; team: Team; /** null = a bot */ name: string | null };
/** Relics (relics.ts) per team, in buy order: each purchase, and whether a holder's death later knocked it out
    (`lost: false` = still held at the end). The same line may appear twice: lost, then bought back. */
export type RelicRecord = { line: RelicLine; lost: boolean };
export type RelicTally = Record<Team, RelicRecord[]>;
/** `relics`: absent from a pre-protocol-14 payload. */
export type MatchStats = { duration: number; heroes: HeroStatLine[]; relics?: RelicTally };

const emptyRelicTally = (): RelicTally => ({ a: [], b: [] });

export const emptyHeroStats = (): HeroStats => ({
  kills: 0, deaths: 0, assists: 0, damageToHeroes: 0, damageTaken: 0, damageToMonsters: 0, healing: 0, shielding: 0,
  captures: 0, pointTime: 0, monsterKills: 0, inkSpent: 0, artifactsBought: 0, bestStreak: 0, passive: 0,
});

export class StatsTracker {
  heroes: HeroStats[] = [];
  relics: RelicTally = emptyRelicTally();
  private streak: number[] = [];
  // Per victim: attacker id → sim time of that attacker's latest hit on it.
  private lastHitBy: Map<HeroId, number>[] = [];

  constructor(private readonly count: number) { this.reset(); }

  reset() {
    this.heroes = Array.from({ length: this.count }, emptyHeroStats);
    this.relics = emptyRelicTally();
    this.streak = new Array(this.count).fill(0);
    this.lastHitBy = Array.from({ length: this.count }, () => new Map());
  }

  heroDamaged(src: HeroId | null, victim: HeroId, hp: number, t: number) {
    if (src !== null) this.lastHitBy[victim]!.set(src, t);  // a hit a shield soaked still earns the assist
    if (hp <= 0) return;
    this.heroes[victim]!.damageTaken += hp;
    if (src !== null) this.heroes[src]!.damageToHeroes += hp;
  }

  monsterDamaged(src: HeroId | null, hp: number) {
    if (src !== null && hp > 0) this.heroes[src]!.damageToMonsters += hp;
  }

  healed(src: HeroId, hp: number) { if (hp > 0) this.heroes[src]!.healing += hp; }
  shielded(src: HeroId, amount: number) { this.heroes[src]!.shielding += amount; }

  /** `killerAlive`: a kill landed after the killer's own death (a pool, a DoT) counts, but starts no streak. */
  died(victim: HeroId, killer: HeroId | null, t: number, killerAlive = true) {
    this.heroes[victim]!.deaths++;
    this.streak[victim] = 0;
    if (killer !== null) {
      const k = this.heroes[killer]!;
      k.kills++;
      if (killerAlive) this.streak[killer]!++;
      k.bestStreak = Math.max(k.bestStreak, this.streak[killer]!);
    }
    for (const [id, at] of this.lastHitBy[victim]!) if (id !== killer && t - at <= ASSIST_WINDOW) this.heroes[id]!.assists++;
    this.lastHitBy[victim]!.clear();
  }

  captured(heroes: HeroId[]) { for (const id of heroes) this.heroes[id]!.captures++; }
  onPoint(id: HeroId, dt: number) { this.heroes[id]!.pointTime += dt; }
  bought(id: HeroId, price: number) { this.heroes[id]!.artifactsBought++; this.heroes[id]!.inkSpent += price; }
  /** A relic is not an artefact: it counts toward the buyer's ink, never `artifactsBought`. */
  relicBought(id: HeroId, team: Team, line: RelicLine, price: number) {
    this.heroes[id]!.inkSpent += price;
    this.relics[team].push({ line, lost: false });
  }
  /** A team holds at most one relic, so the one knocked out is always its latest purchase. */
  relicLost(team: Team) { const last = this.relics[team].at(-1); if (last) last.lost = true; }
  sold(id: HeroId, refund: number) { this.heroes[id]!.inkSpent -= refund; }
  monsterKilled(id: HeroId) { this.heroes[id]!.monsterKills++; }
  passive(id: HeroId, amount: number) { if (amount > 0) this.heroes[id]!.passive += amount; }
}

/** The wire/UI shape: `names[id]` is a human's display name, null (or missing) for a bot. */
export function matchStats(g: { clock: number; heroes: readonly { id: HeroId; kind: HeroKind; team: Team }[]; stats: StatsTracker },
  names: readonly (string | null)[] = []): MatchStats {
  const r = g.stats.relics;
  return {
    duration: g.clock,
    heroes: g.heroes.map(h => ({ ...g.stats.heroes[h.id]!, id: h.id, kind: h.kind, team: h.team, name: names[h.id] ?? null })),
    relics: { a: r.a.map(e => ({ ...e })), b: r.b.map(e => ({ ...e })) },
  };
}
